import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Pause, Pencil, Play, Plus, Square, Trash2 } from 'lucide-react';
import type { TimeEntry } from '../../../lib/tasksApi';
import {
  useAddTimeEntry,
  useDeleteTimeEntry,
  useTimeEntries,
  useUpdateTimeEntry,
} from '../../../hooks/useTimeEntries';
import {
  timerForTask,
  useActiveTimers,
  usePauseTimer,
  useResumeTimer,
  useStartTimer,
  useStopTimer,
  useTaskLiveTimers,
  useTimerNow,
} from '../../../hooks/useTaskTimers';
import { elapsedSeconds, formatElapsed, formatStartedAt } from '../../../lib/timerClock';
import { useAuthStore } from '../../../store/useAuthStore';
import { useI18n } from '../../../contexts/I18nContext';
import { formatEstimate, parseEstimate } from '../../../lib/estimate';
import { DateField } from '../../ui/DateField';
import { useToast } from './Toast';
import { ApiError } from '../../../lib/api';

/**
 * Time tracking (worklog) section for the task detail page — Jira-style hour
 * logging per person. Shows total spent vs. estimate with a progress bar (red
 * on overrun), an inline log form and the entry list. Edit/delete only appear
 * on the current user's own entries, mirroring the backend author-only guard.
 */

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Timer buttons for this task, in the "Time worked" header next to "Log time".
 * State-aware on purpose: a plain Start would 409 against a paused timer.
 */
function TimerButtons({ taskId }: { taskId: string }) {
  const { t } = useI18n();
  const toast = useToast();
  const { data } = useActiveTimers();
  const nowMs = useTimerNow(data?.serverNow);
  const start = useStartTimer();
  const pause = usePauseTimer();
  const resume = useResumeTimer();
  const stop = useStopTimer();

  const timer = timerForTask(data?.timers, taskId);
  const busy = start.isPending || pause.isPending || resume.isPending || stop.isPending;
  const btn =
    'inline-flex items-center gap-1 rounded px-1.5 py-1 text-[12px] text-gray-500 hover:bg-gray-200 hover:text-gray-800 disabled:opacity-50';

  if (!timer) {
    return (
      <button
        type="button"
        disabled={busy}
        // A refused start (no access to the task, or one already running) used
        // to fail silently: the button just did nothing, with no way to tell
        // why. Say what the server said instead.
        onClick={() =>
          start.mutate(taskId, {
            onError: (err) =>
              toast.show(err instanceof ApiError ? err.message : t('timer.startFailed')),
          })
        }
        className={btn}
      >
        <Play size={13} /> {t('timer.start')}
      </button>
    );
  }

  const running = timer.runningSince !== null;
  return (
    <span className="flex items-center gap-1">
      {/* Same green/amber as the global bar, so the two readouts of the one
          timer are obviously the same thing. */}
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          running ? 'animate-pulse bg-green-500' : 'bg-amber-500'
        }`}
      />
      <span
        className={`font-mono text-[13px] font-semibold tabular-nums ${
          running ? 'text-green-600' : 'text-amber-600'
        }`}
      >
        {formatElapsed(elapsedSeconds(timer, nowMs))}
      </span>
      <button
        type="button"
        disabled={busy}
        onClick={() => (running ? pause.mutate(taskId) : resume.mutate(taskId))}
        className={btn}
      >
        {running ? <Pause size={13} /> : <Play size={13} />}
        {running ? t('timer.pause') : t('timer.resume')}
      </button>
      <button type="button" disabled={busy} onClick={() => stop.mutate(taskId)} className={btn}>
        <Square size={13} /> {t('timer.stop')}
      </button>
    </span>
  );
}

/**
 * Colleagues' timers on this task, ticking while they run and marked while
 * paused. The caller's own is left out: it already reads in the header, next to
 * its buttons. A timer that disappears has just become a worklog entry, so the
 * list below is refreshed then instead of waiting for a reload.
 */
function ColleagueTimers({
  taskId,
  currentUserId,
}: {
  taskId: string;
  currentUserId: string | null;
}) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data } = useTaskLiveTimers(taskId);
  const nowMs = useTimerNow(data?.serverNow);
  // Undefined until the first answer, so the first load counts as no change.
  const liveKey = data?.timers.map((timer) => timer.id).join(',');

  const seen = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (liveKey === undefined) return;
    const live = liveKey.split(',');
    const stopped = seen.current?.split(',').some((id) => id && !live.includes(id));
    seen.current = liveKey;
    if (stopped) {
      void qc.invalidateQueries({ queryKey: ['time-entries', taskId] });
      void qc.invalidateQueries({ queryKey: ['task', taskId] });
    }
  }, [liveKey, qc, taskId]);

  const others = (data?.timers ?? []).filter((timer) => timer.user.id !== currentUserId);
  if (others.length === 0) return null;

  return (
    <ul className="mt-2 space-y-1">
      {others.map((timer) => {
        const running = timer.runningSince !== null;
        return (
          <li
            key={timer.id}
            className="flex flex-wrap items-center gap-2 rounded bg-gray-100 px-2 py-1.5 text-[13px]"
          >
            <span
              className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                running ? 'animate-pulse bg-green-500' : 'bg-amber-500'
              }`}
            />
            <span
              className={`font-mono font-semibold tabular-nums ${
                running ? 'text-green-600' : 'text-amber-600'
              }`}
            >
              {formatElapsed(elapsedSeconds(timer, nowMs))}
            </span>
            <span className="text-gray-700">{timer.user.name}</span>
            <span className="text-[12px] text-gray-400">
              {running ? t('time.live.running') : t('time.live.paused')} ·{' '}
              {t('timer.startedAt', { time: formatStartedAt(timer.startedAt) })}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

interface FormState {
  /** A human duration ("45m", "1h 30m"), not decimal hours — see submit(). */
  duration: string;
  date: string;
  note: string;
}

function EntryForm({
  initial,
  isPending,
  onSubmit,
  onCancel,
}: {
  initial: FormState;
  isPending: boolean;
  onSubmit: (values: { hours: number; date: string; note: string }) => void;
  onCancel: () => void;
}) {
  const { t } = useI18n();
  const [form, setForm] = useState<FormState>(initial);
  const [error, setError] = useState<string | null>(null);

  // Stored hours stay decimal (Decimal(6,2)), but nobody thinks in "0.01 h".
  // parseEstimate is the same reader the estimate field uses, so "45m",
  // "1h 30m" and a bare "1.5" all work and round-trip with formatEstimate.
  function submit() {
    const { hours, error: bad } = parseEstimate(form.duration);
    if (bad || hours === null || hours <= 0) {
      setError(t('time.invalidDuration'));
      return;
    }
    setError(null);
    onSubmit({ hours, date: form.date || today(), note: form.note.trim() });
  }

  const inputCls =
    'rounded border border-gray-200 bg-transparent px-2 py-1.5 text-[13px] text-gray-800 placeholder:text-gray-400 focus:border-brand-600 focus:outline-none';

  return (
    <div className="mt-2 rounded-md border border-gray-200 p-3">
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-[11px] text-gray-500">
          {t('time.duration')}
          <input
            autoFocus
            type="text"
            value={form.duration}
            onChange={(e) => setForm((f) => ({ ...f, duration: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
              if (e.key === 'Escape') onCancel();
            }}
            placeholder={t('time.durationPlaceholder')}
            className={`w-28 ${inputCls}`}
          />
        </label>
        <label className="flex flex-col gap-1 text-[11px] text-gray-500">
          {t('time.date')}
          <DateField
            value={form.date}
            onChange={(v) => setForm((f) => ({ ...f, date: v }))}
            className="w-36"
          />
        </label>
        <label className="flex min-w-[160px] flex-1 flex-col gap-1 text-[11px] text-gray-500">
          {t('time.note')}
          <input
            type="text"
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
              if (e.key === 'Escape') onCancel();
            }}
            placeholder={t('time.notePlaceholder')}
            className={inputCls}
          />
        </label>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={submit}
            disabled={isPending}
            className="rounded bg-brand-600 px-2.5 py-1.5 text-[12px] font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {t('time.save')}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="rounded px-2 py-1.5 text-[12px] text-gray-500 hover:bg-gray-200 hover:text-gray-800"
          >
            {t('time.cancel')}
          </button>
        </div>
      </div>
      {error && <p className="mt-1.5 text-[12px] text-red-500">{error}</p>}
    </div>
  );
}

export function TimeTracking({ taskId, estimate }: { taskId: string; estimate: number | null }) {
  const { t } = useI18n();
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);
  const { data, isLoading } = useTimeEntries(taskId);
  const add = useAddTimeEntry(taskId);
  const update = useUpdateTimeEntry(taskId);
  const remove = useDeleteTimeEntry(taskId);

  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const entries = data?.entries ?? [];
  const total = data?.totalHours ?? 0;
  const pct =
    estimate != null && estimate > 0 ? Math.min(100, Math.round((total / estimate) * 100)) : null;
  const overrun = estimate != null && estimate > 0 && total > estimate;

  function submitAdd(values: { hours: number; date: string; note: string }) {
    add.mutate(
      { hours: values.hours, date: values.date, ...(values.note ? { note: values.note } : {}) },
      { onSuccess: () => setAdding(false) },
    );
  }

  function submitEdit(entry: TimeEntry, values: { hours: number; date: string; note: string }) {
    update.mutate(
      { entryId: entry.id, hours: values.hours, date: values.date, note: values.note || null },
      { onSuccess: () => setEditingId(null) },
    );
  }

  return (
    <section className="mt-7">
      {/* Wraps: on a phone the title + "7h of 24h" and the two buttons don't
          fit one line, and the overflow made the whole task page pan sideways. */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <h3 className="flex min-w-0 items-center gap-2 text-[13px] font-semibold text-gray-500">
          {t('time.title')}
          {(total > 0 || estimate != null) && (
            <span className="text-[12px] font-normal text-gray-400">
              {/* formatEstimate, not toFixed(1): a timed session can be 0.01h,
                  which used to render as a meaningless "0.0h". */}
              {formatEstimate(total)}{' '}
              {estimate != null
                ? t('time.ofEstimate', { estimate: formatEstimate(estimate) })
                : `· ${t('time.noEstimate')}`}
            </span>
          )}
        </h3>
        <div className="flex items-center gap-1">
          <TimerButtons taskId={taskId} />
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-[12px] text-gray-500 hover:bg-gray-200 hover:text-gray-800"
          >
            <Plus size={13} /> {t('time.logTime')}
          </button>
        </div>
      </div>

      {pct !== null && (
        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-gray-300">
          <div
            className={`h-full rounded-full ${overrun ? 'bg-red-500' : 'bg-brand-600'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}

      <ColleagueTimers taskId={taskId} currentUserId={currentUserId} />

      {adding && (
        <EntryForm
          initial={{ duration: '', date: today(), note: '' }}
          isPending={add.isPending}
          onSubmit={submitAdd}
          onCancel={() => setAdding(false)}
        />
      )}

      {isLoading && <p className="mt-2 text-[13px] text-gray-400">{t('time.loading')}</p>}

      <ul className="mt-2">
        {entries.map((entry) => {
          const mine = currentUserId !== null && entry.userId === currentUserId;
          if (editingId === entry.id) {
            return (
              <li key={entry.id}>
                <EntryForm
                  initial={{
                    duration: formatEstimate(entry.hours),
                    date: entry.date,
                    note: entry.note ?? '',
                  }}
                  isPending={update.isPending}
                  onSubmit={(values) => submitEdit(entry, values)}
                  onCancel={() => setEditingId(null)}
                />
              </li>
            );
          }
          return (
            <li
              key={entry.id}
              className="group/entry flex items-center gap-2 rounded px-2 py-1.5 text-[13px] transition-colors hover:bg-white/[0.03]"
            >
              <span className="w-14 shrink-0 font-mono text-gray-700">
                {formatEstimate(entry.hours)}
              </span>
              <span className="w-20 shrink-0 font-mono text-[11px] text-gray-400">
                {entry.date}
              </span>
              <span className="shrink-0 text-gray-500">
                {entry.user?.name ?? t('time.deletedUser')}
              </span>
              {entry.note && <span className="truncate text-gray-400">— {entry.note}</span>}
              {mine && (
                <span className="ml-auto flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover/entry:opacity-100">
                  <button
                    type="button"
                    onClick={() => setEditingId(entry.id)}
                    title={t('time.edit')}
                    className="rounded p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-800"
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={() => remove.mutate(entry.id)}
                    title={t('time.delete')}
                    className="rounded p-1 text-gray-500 hover:bg-gray-200 hover:text-red-500"
                  >
                    <Trash2 size={13} />
                  </button>
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {!adding && !isLoading && entries.length === 0 && (
        <p className="mt-1 text-[13px] text-gray-400">{t('time.empty')}</p>
      )}
    </section>
  );
}
